import type { MouseEvent } from "react";
import { Avatar, Box, ButtonBase, Typography, useTheme } from "@mui/material";
import { RecordContextProvider } from "react-admin";
import { DateField } from "../../fields/DateField";
import { Column } from "../../misc/Column";
import { Row } from "../../misc/Row";
import { StudentLabels } from "../../misc/StudentLabels";
import type { Student } from "../../providers/studentsProvider";
import { getExamPlannedDisplayValue } from "./examinationAssignmentUtils";

export function ExaminationAssignmentMobileListItem(props: {
  student: Student;
  onClick: (event: MouseEvent<HTMLDivElement>) => void;
}) {
  const { student, onClick } = props;
  const theme = useTheme();

  return (
    <RecordContextProvider value={student}>
      <ButtonBase
        component="div"
        data-testid="ExaminationAssignmentMobileListItem"
        onClick={onClick}
        role="button"
        tabIndex={0}
        sx={{
          alignItems: "stretch",
          borderBottom: `1px solid ${theme.palette.divider}`,
          boxSizing: "border-box",
          display: "block",
          px: 0,
          py: 1,
          textAlign: "left",
          width: "100%",
        }}
      >
        <Box sx={{ alignItems: "flex-start", display: "flex", gap: 1.5, minWidth: 0 }}>
          <Avatar src={student.avatarUrl} sx={{ flexShrink: 0, height: 40, width: 40 }} />
          <Column sx={{ flex: 1, gap: 0.5, minWidth: 0 }}>
            <Column sx={{ gap: 0.25, minWidth: 0 }}>
              <StudentLabels student={student} size="small" />
              <Typography variant="body1" sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>
                {student.name}
              </Typography>
            </Column>
            <Row sx={{ columnGap: 1.5, flexWrap: "wrap", minWidth: 0, rowGap: 0.25 }}>
              {student.startDate && <DateDetail label="Anmeldung" source="startDate" />}
              {student.examinationAssignmentReceivedAt && (
                <DateDetail label="Eingang" source="examinationAssignmentReceivedAt" />
              )}
            </Row>
            {student.examinationAssignmentExpiresAt && (
              <DateDetail label="Ablauf" source="examinationAssignmentExpiresAt" showDate="with diff" />
            )}
            <Detail label="Prüfungstermin">{getExamPlannedDisplayValue(student)}</Detail>
          </Column>
        </Box>
      </ButtonBase>
    </RecordContextProvider>
  );
}

function DateDetail(props: { label: string; source: string; showDate?: boolean | "with diff" }) {
  const { label, source, showDate } = props;

  return (
    <Detail label={label}>
      <DateField source={source} showDate={showDate} sx={{ fontSize: "inherit", lineHeight: "inherit" }} />
    </Detail>
  );
}

function Detail(props: { children: React.ReactNode; label: string }) {
  const { children, label } = props;

  return (
    <Typography component="div" variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
      <Box component="span" sx={{ fontWeight: 600 }}>
        {label}:
      </Box>{" "}
      {children}
    </Typography>
  );
}
